import React from 'react';

/**
 * Modal for manually mapping an invoice line item to an existing product.
 * Contains a product search field, results list, and selection confirmation.
 */
const MappingModal = ({
  mappingItem,
  searchQuery,
  searchResults,
  searchLoading,
  selectedProduct,
  onSearchChange,
  onProductSelect,
  onClearProduct,
  onSave,
  onClose
}) => {
  if (!mappingItem) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 1050
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '0.375rem',
        boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
        width: '95%', maxWidth: '1000px', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h5 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
            Mapowanie produktu
          </h5>
          <button
            onClick={onClose}
            style={{ background: 'none',  fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          {/* Invoice item info */}
          <div style={{
            padding: '1rem', backgroundColor: 'var(--info-light)',
            border: '1px solid var(--cyan-light)', borderRadius: '0.375rem',
            marginBottom: '1rem'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
              Pozycja faktury: {mappingItem.nazwa_produktu}
            </div>
            {mappingItem.kod_produktu && (
              <div style={{ fontSize: '0.875rem' }}>Kod: {mappingItem.kod_produktu}</div>
            )}
            {mappingItem.ean && (
              <div style={{ fontSize: '0.875rem' }}>EAN: {mappingItem.ean}</div>
            )}
          </div>

          {/* Search input */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Wyszukaj produkt
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={onSearchChange}
                placeholder="Wpisz nazwe, kod lub EAN produktu..."
                style={{
                  width: '100%', padding: '6px 40px 6px 10px',
                  border: '1px solid var(--border)', borderRadius: '6px',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text)',
                  fontSize: '12px', outline: 'none'
                }}
                disabled={!!selectedProduct}
              />
              {searchLoading && (
                <div style={{
                  position: 'absolute', right: '0.75rem', top: '50%',
                  transform: 'translateY(-50%)', color: 'var(--text-muted)'
                }}>
                  ...
                </div>
              )}
              {selectedProduct && (
                <button
                  onClick={onClearProduct}
                  style={{
                    position: 'absolute', right: '0.5rem', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                     color: 'var(--danger)', cursor: 'pointer',
                    fontSize: '1.2rem', padding: '0.25rem'
                  }}
                >
                  &times;
                </button>
              )}
            </div>
          </div>

          {/* Search results */}
          {searchResults.length > 0 && !selectedProduct && (
            <div style={{
              maxHeight: '200px', overflowY: 'auto',
              border: '1px solid var(--border)', borderRadius: '0.375rem',
              marginBottom: '1rem'
            }}>
              {searchResults.map((product, index) => (
                <div
                  key={`product-${product.id}-${index}`}
                  onClick={() => onProductSelect(product)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderBottom: index < searchResults.length - 1 ? '1px solid var(--border)' : 'none',
                    cursor: 'pointer', transition: 'background-color 0.2s',
                    backgroundColor: 'var(--card-bg)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--card-bg)'}
                >
                  <div style={{ fontWeight: '600', marginBottom: '0.125rem', fontSize: '0.875rem' }}>
                    {product.name || product.nazwa}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    {(product.code || product.kod) && `Kod: ${product.code || product.kod} `}
                    {(product.barcode || product.ean) && `| EAN: ${product.barcode || product.ean}`}
                  </div>
                  {(product.stock_quantity !== undefined || product.stock !== undefined) && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--success)', marginTop: '0.125rem' }}>
                      Stan: {product.stock_quantity || product.stock || 0} szt.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Selected product confirmation */}
          {selectedProduct && (
            <div style={{
              padding: '1rem', backgroundColor: 'var(--success-light)',
              border: '1px solid var(--success-light)', borderRadius: '0.375rem',
              marginBottom: '1rem'
            }}>
              <div style={{ fontWeight: '600', color: 'var(--success)', marginBottom: '0.5rem' }}>
                Wybrany produkt
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                <strong>{selectedProduct.name || selectedProduct.nazwa}</strong>
              </div>
              {(selectedProduct.code || selectedProduct.kod) && (
                <div style={{ fontSize: '0.75rem', color: 'var(--success)' }}>
                  Kod: {selectedProduct.code || selectedProduct.kod}
                </div>
              )}
              {(selectedProduct.barcode || selectedProduct.ean) && (
                <div style={{ fontSize: '0.75rem', color: 'var(--success)' }}>
                  EAN: {selectedProduct.barcode || selectedProduct.ean}
                </div>
              )}
            </div>
          )}

          {/* No results */}
          {searchQuery && searchResults.length === 0 && !searchLoading && !selectedProduct && (
            <div style={{
              padding: '1rem', textAlign: 'center', color: 'var(--text-muted)',
              fontSize: '0.875rem', border: '1px solid var(--border)',
              borderRadius: '0.375rem', marginBottom: '1rem'
            }}>
              Brak wynikow wyszukiwania
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: '0.5rem'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 12px', border: '1px solid var(--text-muted)',
              backgroundColor: 'var(--bg)',
              color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px'
            }}
          >
            Anuluj
          </button>
          <button
            onClick={onSave}
            disabled={!selectedProduct}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--info)',
              backgroundColor: selectedProduct ? 'var(--info)' : 'var(--text-muted)',
              color: '#fff' ,
              cursor: selectedProduct ? 'pointer' : 'not-allowed',
              fontSize: '12px',
              opacity: selectedProduct ? 1 : 0.6
            }}
          >
            Zapisz mapowanie
          </button>
        </div>
      </div>
    </div>
  );
};

export default MappingModal;
